import React, { useEffect, useMemo, useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import buildTabs from '../../../utils/tools/buildTabs';
import extraMarketingSchema from '../../../validations/products/extra/marketingSchema';
import useForm from '../../../utils/hooks/useForm';

// Services
import useExtraService from '../../../services/extraService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Extras/Sections/General/General';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Extras/Sections/Medias/Medias';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';

function ExtraMarketing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  // URL query params
  const [searchParams] = useSearchParams();
  const extraId = useMemo(() => searchParams.get('id'), [searchParams]);
  useEffect(() => { if (!extraId) navigate('/products/extras'); }, []);

  const [extra, setExtra] = useState(null);
  const [initialValues, setInitialValues] = useState({
    name: '',
    type: null,
    mainImageMedia: null,
  });

  const { getExtra, putExtra } = useExtraService();

  const getFormattedExtra = (values) => ({
    name: values.name,
    type: values.type ? { value: values.type, label: t(`extras.types.${values.type}`) } : null,
    mainImageMedia: values.mainImageMedia,
  });

  const { isFetching } = useQuery('getExtra', () => getExtra(extraId), {
    enabled: !!extraId,
    onSuccess: (res) => {
      if (res?.data) {
        setExtra(res.data);
        setInitialValues(getFormattedExtra(res.data));
      }
    },
    onError: () => {
      navigate('/products/extras');
    },
  });

  const { isLoading: isPutExtraLoading, mutate: putExtraMutate } = useMutation(
    (values) => putExtra({ extraId, extra: values }),
    {
      onSuccess: (res) => {
        displayNotification({
          message: t('common.successfullySaved'),
        });

        if (res?.data) {
          setInitialValues(getFormattedExtra(res.data));
        }
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'common.saveError'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putExtraMutate({
          name: values.name,
          type: values.type?.value,
          mainImageMediaId: values.mainImageMedia?.id,
        });
      }
    },
    validationSchema: extraMarketingSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'extras.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'extras.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={isFetching ? null : [
          { label: t('extras.title'), href: '/products/extras' },
        ]}
        title={extra?.name}
        isFetching={isFetching}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={extra && !isFetching ? (
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPutExtraLoading}
              disabled={!formik.dirty}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        ) : null}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(ExtraMarketing, { withTabsBar: true }),
    {
      scopes: [SCOPE.update_ownEst_extra, SCOPE.create_ownEst_extra],
    },
  ),
);
